import { F0Dialog, f0FormField, useF0Form, useF0FormDefinition } from '@factorialco/f0-react'
import {
  DuplicateTaskFormDocument,
  TaskFormsDataCollectionDocument,
} from 'generated/resources/graphql'
import { z } from 'zod'

import { FactorialF0Form } from 'modules/core/components/FactorialF0Form'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useActivity } from 'modules/core/lib/activities/activity'
import type { ActivityComponent } from 'modules/core/lib/activities/types'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface DuplicateTaskFormData extends Record<string, string> {
  taskFormId: string
  taskFormName: string
}

export const DuplicateTaskFormActivity: ActivityComponent<DuplicateTaskFormData> = ({
  taskFormId,
  taskFormName,
}) => {
  const { open, closeActivity } = useActivity()

  const [duplicateTaskForm] = useMutation(DuplicateTaskFormDocument, {
    extractorFn: (data) => data.procurementMutations.duplicateProcurementTaskForm,
  })

  const { formRef, submit, isSubmitting, hasErrors } = useF0Form()

  const schema = z.object({
    name: f0FormField(z.string().trim().min(1), {
      label: i18n.t('procurement.purchase_requests.settings.task_forms.create.labels.name'),
    }),
  })

  const formDefinition = useF0FormDefinition({
    name: 'duplicate-task-form',
    schema,
    defaultValues: {
      name: i18n.t(
        'procurement.purchase_requests.settings.task_forms.duplicate_dialog.default_name',
        {
          name: taskFormName,
        }
      ),
    },
    onSubmit: async ({ data }) => {
      const result = await duplicateTaskForm({
        variables: { id: taskFormId, name: data.name },
        refetchQueries: [TaskFormsDataCollectionDocument],
      })

      let fieldErrors: Record<string, string> = {}
      const handled = handleResult(result, (errors) => {
        fieldErrors = errors
        const errorMessage = Object.values(errors).filter(Boolean).join(', ')
        app.setFlash({
          type: 'wrong',
          message: errorMessage || i18n.t('errors.generic_error'),
        })
      })

      if (!handled.success) {
        return { success: false, errors: fieldErrors }
      }

      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      closeActivity()
      return { success: true }
    },
    submitConfig: { type: 'default', hideSubmitButton: true },
  })

  if (!open) return null

  return (
    <F0Dialog
      isOpen
      onClose={closeActivity}
      title={i18n.t('procurement.purchase_requests.settings.task_forms.duplicate_dialog.title')}
      description={i18n.t(
        'procurement.purchase_requests.settings.task_forms.duplicate_dialog.description',
        { name: taskFormName }
      )}
      primaryAction={{
        label: i18n.t('procurement.purchase_requests.settings.task_forms.duplicate_dialog.confirm'),
        loading: isSubmitting,
        disabled: hasErrors,
        onClick: submit,
      }}
      secondaryAction={{
        label: i18n.t('procurement.purchase_requests.settings.task_forms.duplicate_dialog.cancel'),
        disabled: isSubmitting,
        onClick: closeActivity,
      }}
    >
      <FactorialF0Form formRef={formRef} formDefinition={formDefinition} />
    </F0Dialog>
  )
}
